import type { QuickJSAsyncWASMModule } from "quickjs-emscripten-core";
import type { QuickJSAsyncVariant } from "@jitl/quickjs-ffi-types";

let variantPromise: Promise<QuickJSAsyncVariant> | null = null;
let moduleFactoryPromise: Promise<
  typeof import("quickjs-emscripten-core")
> | null = null;

const FALLBACK_DISPOSE_SYMBOL = Symbol.for("Symbol.dispose");

export type PureQuickJsModuleLease = Disposable & {
  module: QuickJSAsyncWASMModule;
};

const loadQuickJsVariant = (): Promise<QuickJSAsyncVariant> => {
  if (!variantPromise) {
    variantPromise = import("@quickjs/async").then(
      (mod) => mod.default as unknown as QuickJSAsyncVariant,
    );
  }

  return variantPromise;
};

const loadQuickJsFactory = () => {
  if (!moduleFactoryPromise) {
    moduleFactoryPromise = import("quickjs-emscripten-core");
  }

  return moduleFactoryPromise;
};

const temporarilyDisableNodeProcessForBrowser = (): (() => void) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }

  const globalWithProcess = globalThis as Record<string, unknown> & {
    process?:
      | {
          versions?: {
            node?: unknown;
          };
        }
      | undefined;
  };

  const originalProcess = globalWithProcess.process;
  const shouldPatch =
    typeof originalProcess === "object" &&
    originalProcess !== null &&
    typeof originalProcess.versions === "object" &&
    originalProcess.versions !== null &&
    "node" in originalProcess.versions;

  if (!shouldPatch) {
    return () => {};
  }

  type ProcessStub = NonNullable<typeof globalWithProcess.process>;

  const placeholderProcess = {
    ...(typeof originalProcess === "object" && originalProcess
      ? (originalProcess as Record<string, unknown>)
      : {}),
    versions: {
      ...(typeof originalProcess?.versions === "object" &&
      originalProcess.versions
        ? (originalProcess.versions as Record<string, unknown>)
        : {}),
    },
  } as ProcessStub;

  if (placeholderProcess?.versions && "node" in placeholderProcess.versions) {
    delete placeholderProcess.versions.node;
  }

  globalWithProcess.process =
    placeholderProcess as typeof globalWithProcess.process;
  return () => {
    globalWithProcess.process = originalProcess;
  };
};

const MAX_POOL_SIZE = 10;

const idleModules: QuickJSAsyncWASMModule[] = [];
let totalModules = 0;
const waiters: Array<(module: QuickJSAsyncWASMModule) => void> = [];

const createQuickJsModule = async (): Promise<QuickJSAsyncWASMModule> => {
  const restoreProcess = temporarilyDisableNodeProcessForBrowser();
  try {
    const [{ newQuickJSAsyncWASMModuleFromVariant }, variant] = await Promise.all(
      [loadQuickJsFactory(), loadQuickJsVariant()],
    );
    return await newQuickJSAsyncWASMModuleFromVariant(variant);
  } finally {
    restoreProcess();
  }
};

const acquireModuleFromPool = async (): Promise<QuickJSAsyncWASMModule> => {
  const existing = idleModules.pop();
  if (existing) {
    return existing;
  }

  if (totalModules < MAX_POOL_SIZE) {
    totalModules += 1;
    try {
      return await createQuickJsModule();
    } catch (error) {
      totalModules -= 1;
      throw error;
    }
  }

  return await new Promise<QuickJSAsyncWASMModule>((resolve) => {
    waiters.push(resolve);
  });
};

const releaseModuleToPool = (module: QuickJSAsyncWASMModule) => {
  const resolve = waiters.shift();
  if (resolve) {
    resolve(module);
    return;
  }
  idleModules.push(module);
};

export const acquirePureQuickJsModule = async (): Promise<PureQuickJsModuleLease> => {
  const module = await acquireModuleFromPool();
  let released = false;

  const release = () => {
    if (released) return;
    released = true;
    releaseModuleToPool(module);
  };

  const lease = { module } as PureQuickJsModuleLease;
  (lease as unknown as Record<symbol, () => void>)[Symbol.dispose] = release;
  (lease as unknown as Record<symbol, () => void>)[FALLBACK_DISPOSE_SYMBOL] =
    release;
  return lease;
};
